import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Building2, Hourglass, Menu, X, Zap } from "lucide-react";
import {
  useAuthStore,
  useIsPendingDriver,
  useSoloDriverMembership,
  useBusinessMemberships,
  useSelectedBusiness,
} from "@/stores/authStore";
import { Logo } from "@/components/ui/Logo";
import { supabase } from "@/lib/supabase";
import { cn } from "@ridereserv/shared/utils";
import { clearUpgradeDismissals, useUpgradeDismissal } from "@/hooks/useUpgradeDismissal";
import { Badge } from "@/components/ui/Badge";
import { UpgradePlanCard } from "@/components/upgrade/UpgradePlanCard";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { useSidebarNavigation } from "@/hooks/useSidebarNavigation";

/**
 * Ink-rail nav row recipe (shared shell chrome — keep in sync with DashboardLayout).
 * @param isActive - Whether the row's route is active.
 * @returns The className string for the nav row.
 */
const navRowClass = (isActive: boolean) =>
  cn(
    "relative flex min-h-11 items-center gap-2.5 rounded-sm px-2 text-sm font-medium transition-colors pointer-fine:min-h-8",
    isActive
      ? "bg-sidebar-active text-sidebar-foreground"
      : "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground"
  );

/**
 * Cobalt active-route indicator rendered inside active nav rows.
 * @returns The indicator element.
 */
const ActiveIndicator = () => (
  <span
    aria-hidden
    className="absolute inset-y-1.5 -left-2 w-0.5 rounded-full bg-sidebar-accent"
  />
);

/**
 * Layout wrapper for personal account/dashboard pages.
 * Available to all authenticated users (customers and drivers).
 * Uses semantic color tokens and Lucide icons from the design system.
 * @returns The account layout component with sidebar navigation
 */
export function AccountLayout() {
  const navigate = useNavigate();
  const { user, memberships, clearAuth, setSelectedBusinessId } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isPendingDriver = useIsPendingDriver();
  const soloDriverMembership = useSoloDriverMembership();
  const businessMemberships = useBusinessMemberships();
  const selectedBusiness = useSelectedBusiness();

  // Check if user is a driver (has 'driver' role in any company)
  const isDriver = memberships.some((m) => m.roles.includes("driver"));

  // Check if user is corporate staff (has admin or manager role in any company)
  // Corporate staff should not see "Become a Driver" prompts - that's for independent users
  const isCorporateStaff = memberships.some((m) =>
    m.roles.some((role) => ["admin", "manager"].includes(role))
  );

  // Show upgrade CTAs only to free solo drivers who don't have admin/manager at a business company
  const isFreeDriver = soloDriverMembership?.subscriptionTierId === "free_driver";
  const hasBusinessAdminRole = businessMemberships.length > 0;
  const showUpgradeCta = isFreeDriver && !hasBusinessAdminRole;

  // Paid solo drivers without a business yet get the "Start a Business" entry
  // point (eligibility mirrors StartBusinessPage: paid_driver tier required).
  const isPaidSoloDriver = soloDriverMembership?.subscriptionTierId === "paid_driver";
  const showStartBusinessCta = isPaidSoloDriver && !hasBusinessAdminRole;

  // Upgrade dismissal state (single dismissal for entire sidebar upgrade section)
  const { isDismissed: isUpgradeDismissed, dismiss: dismissUpgrade } =
    useUpgradeDismissal("sidebar");

  // "Become a Driver" promo dismissal (session-scoped, same treatment as the
  // upgrade CTA). The permanent entry point lives on the Profile page.
  const { isDismissed: isBecomeDriverDismissed, dismiss: dismissBecomeDriver } =
    useUpgradeDismissal("become-driver");

  // Get current business roles for role-based filtering
  const currentBusinessRoles = selectedBusiness?.roles ?? [];
  const hasRole = (roles?: string[]) => {
    if (!roles) return true;
    return roles.some((role) => currentBusinessRoles.includes(role as never));
  };

  // Use shared sidebar navigation configuration for consistency across layouts
  const { personalNavItems, driverNavItems, getBusinessNavItems } = useSidebarNavigation();

  const handleLogout = () => {
    void supabase.auth.signOut().then(() => {
      clearAuth();
      clearUpgradeDismissals();
      void navigate("/login");
    });
  };

  const handleBusinessSwitch = (businessId: string) => {
    setSelectedBusinessId(businessId);
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-neutral-900/60 lg:hidden"
          onClick={() => {
            setSidebarOpen(false);
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-56 transform border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-xl transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:shadow-none",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-full flex-col">
          {/* Brand block */}
          <div className="flex h-12 shrink-0 items-center justify-between border-b border-sidebar-border px-4">
            <Logo asLink size="sm" tone="inverse" />
            <div className="hidden lg:flex">
              <NotificationBell tone="inverse" />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-2 py-3">
            {/* Personal section */}
            <p className="mb-1 px-2 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
              Personal
            </p>
            <ul className="space-y-0.5">
              {personalNavItems.map((item) => (
                <li key={item.href}>
                  <NavLink
                    to={item.href}
                    end={item.href === "/account"}
                    onClick={() => {
                      setSidebarOpen(false);
                    }}
                    className={({ isActive }) => navRowClass(isActive)}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <ActiveIndicator />}
                        <span className="relative shrink-0">
                          <item.icon
                            className={cn("h-4 w-4", isActive && "text-sidebar-accent")}
                            strokeWidth={1.75}
                            aria-hidden="true"
                          />
                          {item.dot && (
                            <span
                              data-testid="unread-dot"
                              aria-hidden="true"
                              className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-destructive-500"
                            />
                          )}
                        </span>
                        <span className="flex-1">{item.label}</span>
                        {item.badge !== undefined && item.badge > 0 && (
                          <span
                            data-testid="notification-badge"
                            className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive-500 px-1.5 text-xs font-medium tabular-nums text-white"
                          >
                            {item.badge > 99 ? "99+" : item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>

            {/* Driver section (shown if user is a driver OR has pending application) */}
            {(isDriver || isPendingDriver) && (
              <>
                <p className="mb-1 mt-6 px-2 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
                  Driver
                </p>
                <ul className="space-y-0.5">
                  {isPendingDriver ? (
                    // Pending application: driver tools are hidden entirely
                    // until approval — the single status entry replaces them.
                    <li>
                      <NavLink
                        to="/account/pending-approval"
                        onClick={() => {
                          setSidebarOpen(false);
                        }}
                        className={({ isActive }) => navRowClass(isActive)}
                      >
                        {({ isActive }) => (
                          <>
                            {isActive && <ActiveIndicator />}
                            <Hourglass
                              className={cn(
                                "h-4 w-4 shrink-0 text-warning-500",
                                isActive && "text-sidebar-accent"
                              )}
                              strokeWidth={1.75}
                              aria-hidden="true"
                            />
                            <span>Approval pending</span>
                          </>
                        )}
                      </NavLink>
                    </li>
                  ) : (
                    driverNavItems.map((item) => (
                      <li key={item.href}>
                        <NavLink
                          to={item.href}
                          onClick={() => {
                            setSidebarOpen(false);
                          }}
                          className={({ isActive }) => navRowClass(isActive)}
                        >
                          {({ isActive }) => (
                            <>
                              {isActive && <ActiveIndicator />}
                              <item.icon
                                className={cn(
                                  "h-4 w-4 shrink-0",
                                  isActive && "text-sidebar-accent"
                                )}
                                strokeWidth={1.75}
                                aria-hidden="true"
                              />
                              <span>{item.label}</span>
                            </>
                          )}
                        </NavLink>
                      </li>
                    ))
                  )}
                </ul>
              </>
            )}

            {/* Become a Driver CTA (only shown if user is NOT a driver, NOT pending, NOT corporate staff, and hasn't dismissed it this session) */}
            {!isDriver && !isPendingDriver && !isCorporateStaff && !isBecomeDriverDismissed && (
              <div className="relative mt-6 rounded-md border border-sidebar-border bg-sidebar-hover p-3">
                <button
                  type="button"
                  onClick={dismissBecomeDriver}
                  className="absolute right-1 top-1 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-sidebar-foreground focus:outline-none focus:ring-2 focus:ring-sidebar-accent"
                  aria-label="Dismiss"
                >
                  <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                </button>
                <p className="pr-7 text-sm font-medium text-sidebar-foreground">
                  Want to earn money?
                </p>
                <p className="mt-1 text-xs text-sidebar-muted">
                  Register as a driver and start accepting rides.
                </p>
                <NavLink
                  to="/account/become-driver"
                  className="mt-3 flex min-h-11 items-center justify-center rounded-sm bg-primary-600 px-4 text-sm font-medium text-white transition-colors hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-sidebar-accent pointer-fine:min-h-8"
                >
                  Become a Driver
                </NavLink>
              </div>
            )}

            {/* Start a Business CTA (paid solo drivers without a business company).
                Uses the "Business" header — it can't clash with the management
                section below, which only renders WITH business memberships. */}
            {showStartBusinessCta && (
              <>
                <p className="mb-1 mt-6 px-2 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
                  Business
                </p>
                <ul className="space-y-0.5">
                  <li>
                    <NavLink
                      to="/account/start-business"
                      onClick={() => {
                        setSidebarOpen(false);
                      }}
                      className={({ isActive }) => navRowClass(isActive)}
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && <ActiveIndicator />}
                          <Building2
                            className={cn("h-4 w-4 shrink-0", isActive && "text-sidebar-accent")}
                            strokeWidth={1.75}
                            aria-hidden="true"
                          />
                          <span>Start a Business</span>
                        </>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {/* Business Management section (only for admin/manager roles) */}
            {businessMemberships.length > 0 && selectedBusiness && (
              <>
                <p className="mb-1 mt-6 px-2 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
                  Business
                </p>
                {/* Business selector */}
                <div className="mb-2">
                  <select
                    value={selectedBusiness.companyId}
                    onChange={(e) => { handleBusinessSwitch(e.target.value); }}
                    className="block min-h-11 w-full rounded-sm border border-sidebar-border bg-sidebar-hover px-2 text-sm text-sidebar-foreground [color-scheme:dark] focus:border-sidebar-accent focus:outline-none focus:ring-1 focus:ring-sidebar-accent pointer-fine:min-h-8"
                  >
                    {businessMemberships.map((membership) => (
                      <option key={membership.companyId} value={membership.companyId}>
                        {membership.companyName}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Business nav items */}
                <ul className="space-y-0.5">
                  {getBusinessNavItems(selectedBusiness.companyId)
                    .filter((item) => hasRole(item.roles))
                    .map((item) => (
                      <li key={item.href}>
                        <NavLink
                          to={item.href}
                          end={item.href === `/${selectedBusiness.companyId}/dashboard`}
                          onClick={() => {
                            setSidebarOpen(false);
                          }}
                          className={({ isActive }) => navRowClass(isActive)}
                        >
                          {({ isActive }) => (
                            <>
                              {isActive && <ActiveIndicator />}
                              <item.icon
                                className={cn(
                                  "h-4 w-4 shrink-0",
                                  isActive && "text-sidebar-accent"
                                )}
                                strokeWidth={1.75}
                                aria-hidden="true"
                              />
                              <span>{item.label}</span>
                            </>
                          )}
                        </NavLink>
                      </li>
                    ))}
                </ul>
              </>
            )}

            {/* Upgrade section - only for free solo drivers without business admin/manager roles */}
            {showUpgradeCta && !isUpgradeDismissed && (
              <>
                <p className="mb-1 mt-6 px-2 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
                  Upgrade
                </p>

                {/* Upgrade Plan nav item */}
                <div className="group relative">
                  <NavLink
                    to="/account/select-plan"
                    onClick={() => {
                      setSidebarOpen(false);
                    }}
                    className={({ isActive }) => navRowClass(isActive)}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <ActiveIndicator />}
                        <Zap
                          className="h-4 w-4 shrink-0 text-upgrade-500"
                          strokeWidth={1.75}
                          aria-hidden="true"
                        />
                        <span className="flex-1">Upgrade Plan</span>
                        <Badge
                          variant="default"
                          className="bg-upgrade-600 text-xs hover:bg-upgrade-600"
                        >
                          Pro
                        </Badge>
                      </>
                    )}
                  </NavLink>
                </div>

                {/* Upgrade card */}
                <div className="mt-2">
                  <UpgradePlanCard location="sidebar" onDismiss={dismissUpgrade} />
                </div>
              </>
            )}
          </nav>

          {/* User section */}
          <div className="border-t border-sidebar-border p-3">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sidebar-hover text-xs font-semibold text-sidebar-foreground">
                {user?.name ? user.name.charAt(0).toUpperCase() : "U"}
              </div>
              <div className="min-w-0 flex-1 truncate">
                <p className="truncate text-sm font-medium text-sidebar-foreground">
                  {user?.name}
                </p>
                <p className="truncate text-xs text-sidebar-muted">
                  {user?.email}
                </p>
              </div>
              {/* Appearance switch — inverse tone for the constant-dark rail */}
              <ThemeToggle tone="inverse" className="shrink-0" />
            </div>
            <div className="mt-3">
              <button
                onClick={handleLogout}
                className="flex min-h-11 w-full items-center justify-center rounded-sm border border-sidebar-border px-3 text-sm font-medium text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-sidebar-foreground focus:outline-none focus:ring-2 focus:ring-sidebar-accent pointer-fine:min-h-8"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar (mobile) */}
        <header className="sticky top-0 z-30 border-b border-neutral-200 bg-surface/95 backdrop-blur lg:hidden">
          <div className="flex h-14 items-center justify-between px-4">
            <button
              type="button"
              onClick={() => {
                setSidebarOpen(true);
              }}
              className="inline-flex h-11 w-11 items-center justify-center rounded-md text-neutral-600 transition-colors hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" strokeWidth={1.75} />
            </button>
            <span className="text-base font-semibold tracking-tight text-neutral-900">
              RideReserv
            </span>
            <div className="flex items-center gap-0.5">
              <ThemeToggle />
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 px-4 py-4 sm:px-6 lg:px-8 lg:py-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
